import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, tap, throwError } from 'rxjs';

import { DownloadError } from '../errors/download.error';

import { InvoiceDownloadApi } from '../api/invoice-download.api';
import type { RegenerateInvoiceResponseDto } from '../api/dtos/invoice-download.dto';
import type {
  DownloadReportParams,
  RegenerateParams,
} from '../models/invoice-download.model';
import { PeriodFormatter } from '../utils/period-formatter';

@Injectable({ providedIn: 'root' })
export class InvoiceDownloadService {
  private readonly _api = inject(InvoiceDownloadApi);
  private readonly _document = inject(DOCUMENT);

  public downloadSurveyFinanceReport(
    params: DownloadReportParams,
    token: string,
  ): Observable<void> {
    const period = `${params.year}${new PeriodFormatter(params.month).padToTwoDigits()}`;
    return this._api
      .surveyFinanceReport(params.companyId, params.companyName, period)
      .pipe(
        tap(({ fileName }) => {
          // Redirect-based download: HTTP headers cannot be set on location.assign(),
          // so the token is passed as a query param. Requires a short-lived token.
          this._document.defaultView!.location.assign(
            `${this._api.surveyFinanceFileUrl(fileName)}?access_token=${token}`,
          );
        }),
        map(() => void 0),
      );
  }

  public downloadSupplierPdf(params: DownloadReportParams): Observable<void> {
    const period = `${params.year}${new PeriodFormatter(params.month).padToTwoDigits()}`;
    const filename = `${params.companyName.replace(/ /g, '')}-${period}.pdf`;
    const isAfterJuly2021 =
      params.year > 2021 || (params.year === 2021 && params.month >= 7);

    const request$ = isAfterJuly2021
      ? this._api.poProjectInvoice({
          cmp: params.companyId,
          date: new PeriodFormatter(params.date).padToTwoDigits(),
          month: new PeriodFormatter(params.month).padToTwoDigits(),
          year: String(params.year),
          invoiceType: params.invoiceType,
          isStatement: true,
        })
      : this._api.surveyFinance({
          company_id: params.companyId,
          company_name: params.companyName,
          pdf_type: 'supplierPdf',
          period,
        });

    return request$.pipe(
      tap((blob) => this._saveFile(blob, filename)),
      map(() => void 0),
      catchError(() => throwError(() => new DownloadError())),
    );
  }

  public downloadBuyerXlsx(params: DownloadReportParams): Observable<void> {
    const isPoOrProject = params.invoiceType !== 'MONTHLY';
    const period = isPoOrProject
      ? `${params.year}${new PeriodFormatter(params.month).padToTwoDigits()}${new PeriodFormatter(params.date).padToTwoDigits()}`
      : `${params.year}${new PeriodFormatter(params.month).padToTwoDigits()}`;
    const filename = `${params.companyName}-${period}.xlsx`;

    return this._api
      .surveyFinanceXlsx({
        company_id: params.companyId,
        company_name: params.companyName,
        pdf_type: 'buyerXlsx',
        period,
        invoiceType: params.invoiceType,
      })
      .pipe(
        tap((blob) => this._saveFile(blob, filename)),
        map(() => void 0),
        catchError(() => throwError(() => new DownloadError())),
      );
  }

  public downloadSupplierXlsx(params: DownloadReportParams): Observable<void> {
    const period = `${params.year}${new PeriodFormatter(params.month).padToTwoDigits()}`;
    const filename = `${params.companyName}-${period}.xlsx`;

    return this._api
      .surveyFinanceXlsx({
        company_id: params.companyId,
        company_name: params.companyName,
        pdf_type: 'supplierXlsx',
        period,
        invoiceType: params.invoiceType,
      })
      .pipe(
        tap((blob) => this._saveFile(blob, filename)),
        map(() => void 0),
        catchError(() => throwError(() => new DownloadError())),
      );
  }

  public downloadRewardCsv(params: DownloadReportParams): Observable<void> {
    const month = new PeriodFormatter(params.month).padToTwoDigits();
    const defaultFilename = `micro_reward_report_${month}_${params.year}.csv`;

    return this._api.rewardCsv(params.companyId, month, params.year).pipe(
      tap((response) => {
        if (!response.body) return;
        const filename = this._extractFilename(
          response.headers,
          defaultFilename,
        );
        this._saveFile(response.body, filename);
      }),
      map(() => void 0),
      catchError(() => throwError(() => new DownloadError())),
    );
  }

  public regenerateInvoice(
    params: RegenerateParams,
  ): Observable<RegenerateInvoiceResponseDto> {
    return this._api.regenerateInvoice({
      cmp: params.companyId,
      date: new PeriodFormatter(params.date).padToTwoDigits(),
      month: new PeriodFormatter(params.month).padToTwoDigits(),
      year: String(params.year),
      invoiceType: params.invoiceType,
    });
  }

  private _extractFilename(
    headers: HttpHeaders,
    defaultFilename: string,
  ): string {
    const contentDisposition = headers.get('Content-Disposition');
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+?)"/);
      if (match) return match[1];
    }
    return defaultFilename;
  }

  private _saveFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = this._document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    this._document.body.appendChild(anchor);
    anchor.click();
    this._document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }
}
